import { MusicProgramFormService } from '@/src/services'
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useToast,
  Heading,
  Textarea,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import ReactDatePicker from '../../common/ReactDatePicker'
import PhoneInput from '../../common/PhoneInput'
import { IFonts } from '@/src/interfaces'

interface IMusicProgramForm {
  activeFontFamily?: IFonts
}

function MusicProgramForm({ activeFontFamily = 'body' }: IMusicProgramForm) {
  const toast = useToast()
  const { locale } = useRouter()

  const { t } = useTranslation()
  const schema = yup.object().shape({
    reservedfullname: yup
      .string()
      .matches(/^[A-za-zğüşiöçĞÜŞİÖÇ\s]/gi, t('form:error.invalidFullname'))
      .required(t('form:error.requiredField')),
    dogum_tarihi: yup.date().required(t('form:error.requiredField')),
    ogrencinin_devam_ettigi_okul_ve_sinifi: yup
      .string()
      .matches(/^[A-za-zğüşiöçĞÜŞİÖÇ\s]/gi, t('form:error.invalidFullname'))
      .required(t('form:error.requiredField')),
    velinin_adi_soyadi: yup
      .string()
      .matches(/^[A-za-zğüşiöçĞÜŞİÖÇ\s]/gi, t('form:error.invalidFullname'))
      .required(t('form:error.requiredField')),
    program_katilim: yup
      .string()
      .matches(/^[A-za-zğüşiöçĞÜŞİÖÇ\s]/gi, t('form:error.invalidFullname'))
      .required(t('form:error.requiredField')),
    veli_e_mail: yup
      .string()
      .email(t('form:error.invalidEmail'))
      .required(t('form:error.requiredField')),
    veli_telefon_no: yup.string().required(t('form:error.requiredField')),
    kvkk: yup.bool().required(t('form:error.requiredField')),
  })
  const { control, register, handleSubmit, errors, reset } = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: true,
  })

  const onSubmit = async (values, e) => {
    e.preventDefault()

    const dogum_tarihi = format(values.dogum_tarihi, 'MM/dd/yyyy')

    try {
      const response = await MusicProgramFormService.post({
        webform_id: 'music_program',
        locale,
        ...values,
        dogum_tarihi,
      })
      if (response) {
        toast({
          title: t('form:success.primary'),
          description: t('form:success.musicprogram'),
          status: 'success',
          duration: 4000,
          isClosable: true,
        })
        reset({})
      }
    } catch (error) {
      toast({
        title: t('form:error.commonTitle'),
        description: t('form:error.commonDescription'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  return (
    <Container width="100%" px="padding.primary">
      <Flex
        justify={{
          base: 'flex-start',
          md: 'center',
        }}
        alignItems={{
          base: 'flex-start',
          md: 'center',
        }}
        mt="padding.primary"
      >
        <Heading
          textAlign={{
            base: 'left',
            md: 'center',
          }}
          fontFamily={activeFontFamily}
          size="lg"
        >
          {t('form:musicprogram.title')}
        </Heading>
      </Flex>
      <Box
        as="form"
        position="relative"
        mt="padding.primary2"
        px={2}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormControl isInvalid={!!errors?.reservedfullname?.message}>
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.reservedfullname')}
          </FormLabel>
          <Input
            type="text"
            name="reservedfullname"
            placeholder={t('form:field.reservedfullname')}
            px="10px"
            border="1px solid"
            borderColor="black.primary3"
            variant="secondary"
            size="secondary"
            focusBorderColor="black.primary3"
            errorBorderColor="red.primary"
            _placeholder={{
              color: 'grey.primary2',
            }}
            ref={register}
          />
          <FormErrorMessage color="red.primary">
            {errors?.reservedfullname?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors?.dogum_tarihi?.message}>
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.dogum_tarihi')}
          </FormLabel>
          <Controller
            control={control}
            name="dogum_tarihi"
            render={({ value, onChange }) => {
              return (
                <ReactDatePicker
                  minDate={null}
                  maxDate={new Date()}
                  filterDate={null}
                  selectedDate={value}
                  onChange={onChange}
                />
              )
            }}
          />
          <FormErrorMessage color="red.primary">
            {errors?.dogum_tarihi?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl
          isInvalid={!!errors?.ogrencinin_devam_ettigi_okul_ve_sinifi?.message}
        >
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.ogrencinin_devam_ettigi_okul_ve_sinifi')}
          </FormLabel>
          <Input
            type="text"
            name="ogrencinin_devam_ettigi_okul_ve_sinifi"
            placeholder={t('form:field.ogrencinin_devam_ettigi_okul_ve_sinifi')}
            px="10px"
            border="1px solid"
            borderColor="black.primary3"
            variant="secondary"
            size="secondary"
            focusBorderColor="black.primary3"
            errorBorderColor="red.primary"
            _placeholder={{
              color: 'grey.primary2',
            }}
            ref={register}
          />
          <FormErrorMessage color="red.primary">
            {errors?.ogrencinin_devam_ettigi_okul_ve_sinifi?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors?.velinin_adi_soyadi?.message}>
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.velinin_adi_soyadi')}
          </FormLabel>
          <Input
            type="text"
            name="velinin_adi_soyadi"
            placeholder={t('form:field.velinin_adi_soyadi')}
            px="10px"
            border="1px solid"
            borderColor="black.primary3"
            variant="secondary"
            size="secondary"
            focusBorderColor="black.primary3"
            errorBorderColor="red.primary"
            _placeholder={{
              color: 'grey.primary2',
            }}
            ref={register}
          />
          <FormErrorMessage color="red.primary">
            {errors?.ogrencinin_devam_ettigi_okul_ve_sinifi?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors?.program_katilim?.message}>
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.program_katilim')}
          </FormLabel>
          <Controller
            control={control}
            name="program_katilim"
            render={({ value, onChange }) => (
              <Textarea value={value} onChange={onChange} rows={10} />
            )}
          />
          <FormErrorMessage color="red.primary">
            {errors?.program_katilim?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors?.veli_e_mail?.message}>
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.veli_e_mail')}
          </FormLabel>
          <Input
            type="email"
            name="veli_e_mail"
            placeholder={t('form:field.veli_e_mail')}
            px="10px"
            border="1px solid"
            borderColor="black.primary3"
            variant="secondary"
            size="secondary"
            focusBorderColor="black.primary3"
            errorBorderColor="red.primary"
            _placeholder={{
              color: 'grey.primary2',
            }}
            ref={register}
          />
          <FormErrorMessage color="red.primary">
            {errors?.veli_e_mail?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors?.veli_telefon_no?.message}>
          <FormLabel
            fontWeight="bold"
            fontSize="md"
            fontFamily={activeFontFamily}
            m="2"
          >
            {t('form:field.veli_telefon_no')}
          </FormLabel>
          <Controller
            control={control}
            name="veli_telefon_no"
            render={({ value, onChange }) => (
              <PhoneInput
                placeholder={t('form:field.veli_telefon_no')}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <FormErrorMessage color="red.primary">
            {errors?.veli_telefon_no?.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl my={10} isInvalid={!!errors?.kvkk?.message}>
          <CheckboxGroup>
            <Flex flexDirection="column">
              <Checkbox name="kvkk" ref={register} size="md">
                {t('form:field.kvkk')}
              </Checkbox>
            </Flex>
          </CheckboxGroup>
          <FormErrorMessage color="red.primary">
            {errors?.kvkk?.message}
          </FormErrorMessage>
        </FormControl>

        <Button
          name="muzicprogrami"
          mb={4}
          height="50px"
          type="submit"
          variant="secondary"
          my="padding.primary"
        >
          {t('common:submit')}
        </Button>
      </Box>
    </Container>
  )
}

export default MusicProgramForm
